import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { TooltipProvider } from '@/components/ui/tooltip';
import { initWebVitals } from './utils/webVitals';
import { STALE_TIMES } from './types/statcast';
import './index.css';
import App from './App.tsx';

// Initialize Sentry for error tracking and alerting
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' or 'production'
    // Sample rate for performance monitoring (10% of page loads)
    tracesSampleRate: 0.1,
    // Capture 100% of errors
    sampleRate: 1.0,
    // Only send errors in production
    enabled: import.meta.env.PROD,
    // Ignore common non-actionable errors
    ignoreErrors: [
      // Browser extensions
      /extensions\//i,
      /^chrome-extension:\/\//i,
      // Network errors (user's connection)
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // User navigation
      'ResizeObserver loop',
    ],
  });
}

// Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  console.warn('Missing VITE_CLERK_PUBLISHABLE_KEY - authentication will not work');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Standings data - cache for 1 hour (changes slowly)
      staleTime: STALE_TIMES.TEAM_STATS,
      // Keep data in cache for 2 hours
      gcTime: 1000 * 60 * 60 * 2, // 2 hours (formerly cacheTime)
      // Retry failed requests with exponential backoff (1s, 2s, 4s)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
      // Refetch on window focus (user comes back to tab)
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY || ''}>
      <QueryClientProvider client={queryClient}>
        <FeatureFlagProvider>
          <TooltipProvider delayDuration={300}>
            <App />
          </TooltipProvider>
        </FeatureFlagProvider>
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>
);

// Register service worker for push notifications
import { registerServiceWorker } from '@/lib/serviceWorker';
registerServiceWorker();

// Initialize Web Vitals tracking for performance monitoring
initWebVitals();
